import axios from 'axios'
import { useState } from 'react'
import { apiStatus } from '../constants/apiStatus'
import { apiUrl, axiosDefaultHeader, baseUrl, stage } from '../constants/apiUrl'
import { loadStatus } from '../constants/loadStatus'
import { errorMsg } from '../constants/messages'
import { useAuth } from './useAuth'

interface MyInfo {
  role: number
}

interface ResultMyInfo {
  success: boolean
  info?: MyInfo
  message?: string
}

interface C109MyInfoGetProps {
  execMyInfoGet: (tenant: string) => Promise<ResultMyInfo>
  loading: loadStatus
}

const MY_INFO_DEDUPE_MS = 1500
const myInfoInFlight = new Map<string, Promise<ResultMyInfo>>()
const myInfoRecent = new Map<string, { expiresAt: number; result: ResultMyInfo }>()

// C109 DCP 自身情報取得API呼出
export const useC109MyInfoGet = (): C109MyInfoGetProps => {
  const [loading, setLoading] = useState(loadStatus.NONE)
  const auth = useAuth()

  const execMyInfoGet = async (tenant: string) => {
    const cached = myInfoRecent.get(tenant)
    if (cached && cached.expiresAt > Date.now()) {
      return cached.result
    }

    const inFlight = myInfoInFlight.get(tenant)
    if (inFlight) {
      return inFlight
    }

    const requestPromise = (async (): Promise<ResultMyInfo> => {
      try {
        setLoading(loadStatus.LOADING)
        const jwt = await auth.getJwt()
        if (jwt === '') {
          return { success: false, message: errorMsg.SESSION_ERROR }
        }

        const header = Object.assign(axiosDefaultHeader, {
          Authorization: 'Bearer ' + jwt,
        })

        const response = await axios.get(
          `${baseUrl}${stage}${apiUrl.tenantsPath}${tenant}${apiUrl.usersPath}me`,
          {
            headers: header,
            validateStatus: () => true,
          },
        )

        if (response.status === apiStatus.SUCCESS) {
          return { success: true, info: response.data }
        }

        if (response.data && response.data.message) {
          return { success: false, message: response.data.message }
        }
        return { success: false, message: errorMsg.SYSTEM_ERROR }
      } catch (error) {
        return { success: false, message: errorMsg.SYSTEM_ERROR }
      } finally {
        setLoading(loadStatus.LOADED)
      }
    })()

    myInfoInFlight.set(tenant, requestPromise)

    try {
      const result = await requestPromise
      myInfoRecent.set(tenant, {
        expiresAt: Date.now() + MY_INFO_DEDUPE_MS,
        result,
      })
      return result
    } finally {
      myInfoInFlight.delete(tenant)
    }
  }

  return { execMyInfoGet, loading }
}
